import { useCallback, useEffect } from 'react';

import { type SubmitHandler, useForm } from 'react-hook-form';

import { useDisclosure } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { isNotDefined } from '@enterprise/core';

import { nthQueryKeys } from '@components/nth-calculator/common/nth-query-keys';
import { useUpdateMailing } from '@hooks/mutations/update-mailing.hook';

import type { PackageTargetEditorConfigurationOptions } from './types/package-target-editor-configuration.options';
import type { PackageTargetForm } from './types/package-target-form.type';

export const usePackageTargetEditorConfiguration = ({
  mailingId,
  packageTarget,
}: PackageTargetEditorConfigurationOptions) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const queryClient = useQueryClient();

  const { control, formState, handleSubmit, reset } = useForm<PackageTargetForm>({
    defaultValues: { targetQuantity: packageTarget },
    mode: 'onChange',
  });

  const { errors, isDirty, isValid } = formState;

  const { isPending: isSaving, mutate: updateMailing } = useUpdateMailing({
    onSuccess: () => {
      if (isNotDefined(mailingId)) {
        return;
      }

      void queryClient.invalidateQueries({ queryKey: nthQueryKeys.twentileCounts(mailingId) });
      onClose();
    },
  });

  const isSaveDisabled = isNotDefined(mailingId) || isSaving || !isDirty || !isValid;

  const onSubmit: SubmitHandler<PackageTargetForm> = ({ targetQuantity }) => {
    if (isNotDefined(mailingId) || isNotDefined(targetQuantity)) {
      return;
    }

    updateMailing({
      mailing: { id: mailingId, targetQuantity },
      mailingId,
    });
  };

  const handleOpen = useCallback(() => {
    reset({ targetQuantity: packageTarget });
    onOpen();
  }, [onOpen, packageTarget, reset]);

  const handleCancel = useCallback(() => {
    reset({ targetQuantity: packageTarget });
    onClose();
  }, [onClose, packageTarget, reset]);

  useEffect(() => {
    if (!isOpen) {
      reset({ targetQuantity: packageTarget });
    }
  }, [isOpen, packageTarget, reset]);

  return {
    control,
    errors,
    handleCancel,
    handleOpen,
    handleSubmit,
    isOpen,
    isSaveDisabled,
    isSaving,
    onSubmit,
  };
};
